/**
 * Umrechnung zwischen Ortszeit Europe/Berlin und absoluten Zeitpunkten. Termine werden in Ortszeit erfasst
 * (Terminliste, Formular), gespeichert aber als UTC-ISO-String (`toISOString()`), damit sich Zeitpunkte als Text
 * vergleichen und sortieren lassen.
 */

const ZONE = 'Europe/Berlin';

const teileFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export interface Ortszeit {
  jahr: number;
  monat: number;
  tag: number;
  stunde: number;
  minute: number;
}

/** Ortszeit Berlin zu einem Zeitpunkt. */
export function ortszeit(zeitpunkt: Date): Ortszeit {
  const teile = Object.fromEntries(
    teileFormat.formatToParts(zeitpunkt).map((t) => [t.type, Number(t.value)]),
  );
  return {
    jahr: teile.year!,
    monat: teile.month!,
    tag: teile.day!,
    stunde: teile.hour!,
    minute: teile.minute!,
  };
}

/** Abstand Berlin – UTC in Minuten zum Zeitpunkt `ms` (60 im Winter, 120 im Sommer). */
function versatzMinuten(ms: number): number {
  const o = ortszeit(new Date(ms));
  return (Date.UTC(o.jahr, o.monat - 1, o.tag, o.stunde, o.minute) - ms) / 60_000;
}

/**
 * Zeitpunkt zu einer Ortszeit in Berlin, als UTC-ISO-String. `undefined`, wenn das Datum nicht existiert
 * (z. B. 31.02.) oder die Uhrzeit bei der Umstellung auf Sommerzeit ausfällt.
 */
export function berlinZuIso(o: Ortszeit): string | undefined {
  const alsUtc = Date.UTC(o.jahr, o.monat - 1, o.tag, o.stunde, o.minute);
  // Zweimal annähern: der Versatz kann sich zwischen Schätzung und Ergebnis ändern (Umstellungstage).
  let ms = alsUtc - versatzMinuten(alsUtc) * 60_000;
  ms = alsUtc - versatzMinuten(ms) * 60_000;
  const zurueck = ortszeit(new Date(ms));
  const gleich =
    zurueck.jahr === o.jahr &&
    zurueck.monat === o.monat &&
    zurueck.tag === o.tag &&
    zurueck.stunde === o.stunde &&
    zurueck.minute === o.minute;
  return gleich ? new Date(ms).toISOString() : undefined;
}

const zwei = (n: number) => String(n).padStart(2, '0');

/** Für Formularfelder: `{ datum: '2027-02-17', uhrzeit: '13:00' }` in Ortszeit Berlin. */
export function isoZuFormular(iso: string): { datum: string; uhrzeit: string } {
  const o = ortszeit(new Date(iso));
  return {
    datum: `${o.jahr}-${zwei(o.monat)}-${zwei(o.tag)}`,
    uhrzeit: `${zwei(o.stunde)}:${zwei(o.minute)}`,
  };
}

/** Gegenstück zu `isoZuFormular`. `undefined` bei ungültiger Eingabe. */
export function formularZuIso(datum: string, uhrzeit: string): string | undefined {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum);
  const u = /^(\d{1,2}):(\d{2})$/.exec(uhrzeit);
  if (!d || !u) return undefined;
  return berlinZuIso({
    jahr: Number(d[1]),
    monat: Number(d[2]),
    tag: Number(d[3]),
    stunde: Number(u[1]),
    minute: Number(u[2]),
  });
}
